import { useState, useEffect, useRef } from 'react'
import { PlanItem, DestinationLicense, plans, DestinationUserSearchResult } from '@/api/client'
import { DestinationUserPicker } from './DestinationUserPicker'
import { ContentScopePicker } from './ContentScopePicker'
import { Loader2, Pencil } from 'lucide-react'

interface Props {
  projectId: string
  item: PlanItem
  licenses: DestinationLicense[]
  defaultDestinationDomain: string | null
  onUpdated: (item: PlanItem) => void
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '–'
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return gb.toFixed(1) + ' GB'
  const mb = bytes / (1024 * 1024)
  return mb.toFixed(0) + ' MB'
}

function generateUpn(sourceUpn: string, destinationDomain: string | null): string {
  if (!destinationDomain) return ''
  const at = sourceUpn.indexOf('@')
  if (at < 0) return ''
  const prefix = sourceUpn.slice(0, at)
  const domain = sourceUpn.slice(at + 1)
  const suffix = domain.split('.')[0]
  return `${prefix}.${suffix}@${destinationDomain}`.toLowerCase()
}

export function PlanItemRow({ projectId, item, licenses, defaultDestinationDomain, onUpdated }: Props) {
  const [saving, setSaving] = useState(false)
  const [editingUpn, setEditingUpn] = useState(false)
  const [upnDraft, setUpnDraft] = useState(item.destinationUpn || generateUpn(item.sourceUpn, defaultDestinationDomain))
  const upnInputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Quando muda defaultDomain, regenera UPN se Não foi customizado pelo operador
  useEffect(() => {
    if (!item.destinationUpn && defaultDestinationDomain) {
      const generated = generateUpn(item.sourceUpn, defaultDestinationDomain)
      setUpnDraft(generated)
    }
  }, [defaultDestinationDomain, item.sourceUpn, item.destinationUpn])

  useEffect(() => {
    if (editingUpn && upnInputRef.current) {
      upnInputRef.current.focus()
      upnInputRef.current.select()
    }
  }, [editingUpn])

  async function patch(data: any) {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSaving(true)
    try {
      const updated = await plans.patchItem(projectId, item.id, data)
      onUpdated(updated)
    } finally {
      setSaving(false)
    }
  }

  function debouncedPatch(data: any, delay = 600) {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => patch(data), delay)
  }

  function toggleSelected() {
    patch({ isSelected: !item.isSelected })
  }

  function changeStrategy(s: 'CreateNew' | 'LinkExisting') {
    patch({ strategy: s })
  }

  function commitUpn() {
    if (upnDraft.trim() && upnDraft !== item.destinationUpn) {
      patch({ destinationUpn: upnDraft.trim() })
    }
    setEditingUpn(false)
  }

  function changeLicense(skuId: string) {
    if (!skuId) {
      patch({ assignedLicenseSkuId: '', assignedLicenseSkuName: '' })
      return
    }
    const lic = licenses.find(l => l.skuId === skuId)
    if (lic) {
      patch({
        assignedLicenseSkuId: lic.skuId,
        assignedLicenseSkuName: lic.friendlyName || lic.skuPartNumber,
      })
    }
  }

  function changeContent(json: string) {
    debouncedPatch({ contentSelectionJson: json }, 400)
  }

  function linkUser(user: DestinationUserSearchResult | null) {
    if (user) {
      patch({
        strategy: 'LinkExisting',
        linkedDestinationUserId: user.id,
        linkedDestinationUpn: user.userPrincipalName,
      })
    } else {
      patch({ strategy: 'CreateNew', linkedDestinationUserId: null, linkedDestinationUpn: null })
    }
  }

  const totalBytes = (item.mailboxSizeBytes || 0) + (item.oneDriveSizeBytes || 0)

  return (
    <tr className={item.isSelected ? 'bg-blue-50/30' : ''}>
      <td className="p-2 align-middle">
        <input
          type="checkbox"
          checked={item.isSelected}
          onChange={toggleSelected}
          disabled={saving}
        />
      </td>

      <td className="p-2 align-middle">
        <div className="text-xs font-medium text-slate-900 truncate max-w-[200px]" title={item.sourceUpn}>
          {item.sourceUpn}
        </div>
        {item.sourceDisplayName && (
          <div className="text-[11px] text-slate-500 truncate max-w-[200px]">{item.sourceDisplayName}</div>
        )}
      </td>

      <td className="p-2 align-middle text-xs text-slate-600 whitespace-nowrap">
        {formatBytes(item.mailboxSizeBytes)} / {formatBytes(item.oneDriveSizeBytes)}
        {totalBytes > 0 && (
          <div className="text-[10px] text-slate-400">total {formatBytes(totalBytes)}</div>
        )}
      </td>

      <td className="p-2 align-middle">
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-[11px]">
          <button
            type="button"
            disabled={!item.isSelected || saving}
            onClick={() => changeStrategy('CreateNew')}
            className={`px-2 py-0.5 ${item.strategy === 'CreateNew' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700'} disabled:opacity-50`}
          >
            Nova
          </button>
          <button
            type="button"
            disabled={!item.isSelected || saving}
            onClick={() => changeStrategy('LinkExisting')}
            className={`px-2 py-0.5 ${item.strategy === 'LinkExisting' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700'} disabled:opacity-50`}
          >
            Vincular
          </button>
        </div>
      </td>

      <td className="p-2 align-middle min-w-[220px]">
        {item.strategy === 'CreateNew' ? (
          editingUpn ? (
            <input
              ref={upnInputRef}
              type="text"
              className="input !py-1 !text-xs !min-h-0"
              value={upnDraft}
              onChange={(e) => setUpnDraft(e.target.value)}
              onBlur={commitUpn}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitUpn()
                if (e.key === 'Escape') { setUpnDraft(item.destinationUpn || ''); setEditingUpn(false) }
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => { setUpnDraft(item.destinationUpn || generateUpn(item.sourceUpn, defaultDestinationDomain)); setEditingUpn(true) }}
              className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 truncate max-w-[220px]"
              disabled={!item.isSelected}
            >
              <span className="truncate">{item.destinationUpn || generateUpn(item.sourceUpn, defaultDestinationDomain) || '(definir UPN)'}</span>
              <Pencil className="h-3 w-3 text-slate-400 shrink-0" />
            </button>
          )
        ) : (
          <DestinationUserPicker
            projectId={projectId}
            selectedUserId={item.linkedDestinationUserId}
            selectedUpn={item.linkedDestinationUpn}
            onSelect={linkUser}
            disabled={!item.isSelected || saving}
          />
        )}
      </td>

      <td className="p-2 align-middle">
        <select
          className="input !py-1 !text-xs !min-h-0 !w-auto"
          value={item.assignedLicenseSkuId || ''}
          onChange={(e) => changeLicense(e.target.value)}
          disabled={!item.isSelected || saving}
        >
          <option value="">— sem licença —</option>
          {licenses.map(l => (
            <option key={l.skuId} value={l.skuId}>
              {l.friendlyName || l.skuPartNumber} ({l.availableUnits} disp.)
            </option>
          ))}
        </select>
      </td>

      {/* Endereço final: trava o aviso ao usuário até a conta estar com o nome definitivo.
          Vazio = sem trava (migração que não troca de domínio). */}
      <td className="p-2 align-middle">
        <input
          type="text"
          defaultValue={item.finalUpn || ''}
          placeholder="(igual ao destino)"
          disabled={!item.isSelected || saving}
          onBlur={e => {
            const v = e.target.value.trim()
            if (v !== (item.finalUpn || '')) patch({ finalUpn: v })
          }}
          title="Endereço que a conta terá ao fim da migração. O aviso de OneNote espera por ele, porque o link do OneDrive acompanha o UPN. Vazio = usa o endereço de destino."
          className="w-56 px-2 py-1 text-xs border border-slate-300 rounded disabled:bg-slate-50 disabled:text-slate-400"
        />
      </td>

      <td className="p-2 align-middle">
        <ContentScopePicker
          json={item.contentSelectionJson}
          onChange={changeContent}
          disabled={!item.isSelected || saving}
        />
      </td>

      <td className="p-2 align-middle text-right w-8">
        {saving && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
      </td>
    </tr>
  )
}
